import { conditionInterpretator } from './scene_conditions_to_ui';
import { actionInterpretator } from './scene_actions_to_ui';
import { parseRunScriptFromSceneAction } from 'src/helpers/device_script_action_helpers';
import type { DeviceActionScript } from 'src/types/ScenesActionsTypes';
import { asSceneList } from './scene_data_helpers';
import { getDelayedSceneAction, getSceneActionPurpose } from './get_delayed_scene_action';
import type { DeviceCondition_UI } from '../../../types/DeviceCondition_UI';
import { _findOrIndex } from '../../../helpers/handle_manage_old_scenes';
import { is_device_cond_op_in_r } from 'src/types/ScenesConditionsTypes';
import { isIslandTriggerGateClause } from 'src/components/Flows/converters/flow-island-utils';

const FLOW_EXPR_OPS = ['eq', 'neq', 'lt', 'gt', 'le', 'ge'] as const;

function isFlowReactiveSubscription(element: unknown): boolean {
	if (!element || typeof element !== 'object') return false;
	const neq = (element as { neq?: unknown }).neq;
	return Array.isArray(neq) && typeof (neq[0] as { rr?: unknown })?.rr === 'string' && neq[1] === null;
}

function isFlowExpressionDeviceTrigger(element: unknown): boolean {
	if (!element || typeof element !== 'object') return false;
	const el = element as Record<string, unknown>;
	for (const op of FLOW_EXPR_OPS) {
		const args = el[op];
		if (
			Array.isArray(args) &&
			typeof (args[0] as { rr?: unknown })?.rr === 'string' &&
			(['dev:', 'var:'] as const).some((prefix) =>
				String((args[0] as { rr: string }).rr).startsWith(prefix),
			)
		) {
			return true;
		}
	}
	return false;
}

const flatten_condition_nodes = (value): any[] => {
	if (!value) return [];
	if (Array.isArray(value)) {
		return value.flatMap((entry) => flatten_condition_nodes(entry));
	}
	if (typeof value !== 'object') return [];

	const obj = value as any;
	const out: any[] = [];
	const hasFlowExpressionShape = FLOW_EXPR_OPS.some((op) => Array.isArray(obj[op]));
	// Tagged GUI conditions such as a daily schedule own their compound `and`/`or`
	// expression. The interpreter expects the complete object, not its internal
	// time and weekday clauses as separate conditions.
	if (obj._gui_type && (Array.isArray(obj.or) || Array.isArray(obj.and))) {
		return [obj];
	}

	if (Array.isArray(obj.or)) {
		out.push(...obj.or.flatMap((entry) => flatten_condition_nodes(entry)));
	}
	if (Array.isArray(obj.and)) {
		out.push(...obj.and.flatMap((entry) => flatten_condition_nodes(entry)));
	}

	// Leaf condition object (e.g. rr/r/cmpop etc.); schedule containers with
	// indailyint are intentionally ignored by conditionInterpretator.
	const hasExprShape =
		hasFlowExpressionShape ||
		Object.prototype.hasOwnProperty.call(obj, 'rr') ||
		Object.prototype.hasOwnProperty.call(obj, 'r') ||
		Object.prototype.hasOwnProperty.call(obj, 'cmpop') ||
		Object.prototype.hasOwnProperty.call(obj, 'thsop') ||
		Object.prototype.hasOwnProperty.call(obj, '_gui_type') ||
		Object.prototype.hasOwnProperty.call(obj, '_gui_purpose');
	if (hasExprShape && !Object.prototype.hasOwnProperty.call(obj, 'indailyint')) {
		out.push(obj);
	}

	return out;
};

/** Expand `do` / `else` branch content: array, single action, or `{ blk: [...] }` (flow multi-action branches). */
function normalizeActionBranchStatements(raw: unknown): any[] {
	if (raw == null) return [];
	if (Array.isArray(raw)) return raw;
	if (typeof raw === 'object') {
		const blk = (raw as { blk?: unknown }).blk;
		if (Array.isArray(blk)) return blk;
		return [raw];
	}
	return [];
}

function normalizeSceneDoActionList(doBlock: unknown): any[] {
	if (doBlock == null) return [];
	if (Array.isArray(doBlock)) return doBlock;
	if (typeof doBlock === 'object' && Array.isArray((doBlock as { blk?: unknown }).blk)) {
		return (doBlock as { blk: any[] }).blk;
	}
	return [doBlock];
}

/** Collect every `if` subtree used for conditions (top-level + nested flow islands). */
function collectSceneConditionRoots(scene: { if?: unknown; do?: unknown }): unknown[] {
	const roots: unknown[] = [];
	if (scene.if != null) roots.push(scene.if);

	const walkActions = (actions: any[]) => {
		for (const element of actions) {
			if (!element || typeof element !== 'object') continue;

			if (element._gui_island_gate === true) {
				if (element.if != null) roots.push(element.if);
				const inner = element.do;
				if (inner && typeof inner === 'object' && (inner as { if?: unknown }).if != null) {
					roots.push((inner as { if: unknown }).if);
					walkActions(normalizeActionBranchStatements((inner as { do?: unknown }).do));
					walkActions(normalizeActionBranchStatements((inner as { else?: unknown }).else));
				}
				continue;
			}

			if (
				element.if != null &&
				(element.do !== undefined || element.else !== undefined) &&
				!element._gui_type &&
				!element._gui_purpose
			) {
				roots.push(element.if);
				walkActions(normalizeActionBranchStatements(element.do));
				walkActions(normalizeActionBranchStatements(element.else));
				continue;
			}

			if (typeof element.wait === 'number' && element.do != null) {
				walkActions(normalizeActionBranchStatements(element.do));
			}
		}
	};

	walkActions(normalizeSceneDoActionList(scene.do));
	return roots;
}

export const check_for_broken_conditions = (conditions) => {
	const conditionLeaves = flatten_condition_nodes(conditions);
	if (conditionLeaves.length === 0) return true;
	for (let element of conditionLeaves) {
		// Reactive subscription emitted by the flow editor for root nodes:
		// { neq: [{ rr: "dev:..." | "dt:t" }, null], _gui_purpose: "condition" }
		// The null comparison subscribes the scene to resource changes/ticks.
		// Not interpretable by the old UI format checkers but structurally valid.
		if (element?._gui_purpose === 'condition' && isFlowReactiveSubscription(element)) {
			continue;
		}
		// Flow per-island trigger gate (trigonly + property match on _trig_reason).
		if (isIslandTriggerGateClause(element)) {
			continue;
		}
		// Flow expression condition used by island triggers and switch cases:
		// { eq: [{ rr: "dev:…" }, value] }. Switch cases intentionally have no
		// _gui_purpose marker because they are structural if/do/else conditions.
		if (isFlowExpressionDeviceTrigger(element)) {
			continue;
		}
		// Flow editor device conditions: { r, cmpop, cmpval, _gui_purpose: "condition" }
		if (element?._gui_purpose === 'condition' && is_device_cond_op_in_r(element)) {
			continue;
		}
		let purpose = element?._gui_type || element?._gui_purpose || undefined;
		let tr = conditionInterpretator(element, purpose);
		/** If the interpratator does not match any known formats, then it is a broken condition */
		if (tr === undefined) {
			return true;
		}
	}
	return false;
};

export const check_for_broken_actions = (actions) => {
	const list = asSceneList(actions);
	if (list.length === 0) return true;
	for (let element of list) {
		// First-class Flow Action Sequence: the wrapper is structural; validate
		// its ordered block recursively instead of treating it as an action.
		if (
			element &&
			typeof element === 'object' &&
			element._actionSequenceNodeId &&
			Array.isArray(element.blk)
		) {
			if (element.blk.length === 0 || check_for_broken_actions(element.blk)) return true;
			continue;
		}

		// Flow multi-island outer guard: validate gate `if`, then the real island body.
		if (element && typeof element === 'object' && element._gui_island_gate === true) {
			if (element.if != null && check_for_broken_conditions(element.if)) return true;
			const inner = element.do;
			if (inner && typeof inner === 'object') {
				if (
					(inner as { if?: unknown }).if != null &&
					check_for_broken_conditions((inner as { if: unknown }).if)
				) {
					return true;
				}
				const nestedDo = normalizeActionBranchStatements((inner as { do?: unknown }).do);
				if (nestedDo.length > 0 && check_for_broken_actions(nestedDo)) return true;
				const nestedElse = normalizeActionBranchStatements((inner as { else?: unknown }).else);
				if (nestedElse.length > 0 && check_for_broken_actions(nestedElse)) return true;
			}
			continue;
		}

		// Structural if/do/else block (produced by switch nodes) — recursively
		// validate the nested branches instead of running actionInterpretator.
		if (
			element &&
			typeof element === 'object' &&
			element.if &&
			!element._gui_type &&
			!element._gui_purpose
		) {
			if (check_for_broken_conditions(element.if)) return true;
			const nestedDo = normalizeActionBranchStatements(element.do);
			if (nestedDo.length > 0 && check_for_broken_actions(nestedDo)) return true;
			const nestedElse = normalizeActionBranchStatements(element.else);
			if (nestedElse.length > 0 && check_for_broken_actions(nestedElse)) return true;
			continue;
		}

		// Delay-then-action from flow timer / delayed action: { wait, units?, wid?, do: action | { blk: [...] } }
		if (
			element &&
			typeof element === 'object' &&
			typeof element.wait === 'number' &&
			element.do != null &&
			typeof element.do === 'object'
		) {
			const nestedDo = normalizeActionBranchStatements(element.do);
			if (nestedDo.length === 0) return true;
			if (check_for_broken_actions(nestedDo)) return true;
			continue;
		}

		const delayedAction = getDelayedSceneAction(element);
		const e = delayedAction !== undefined ? delayedAction : element;
		if (parseRunScriptFromSceneAction(e as DeviceActionScript)) {
			continue;
		}

		const purpose = getSceneActionPurpose(element, delayedAction);
		let tr = actionInterpretator(purpose, e, element.msg_type);

		/** If the interpratator does not match any known formats, then it is a broken action */
		if (tr === undefined) {
			return true;
		}
	}

	return false;
};

export const check_for_broken_conditions_or_actions = (scene) => {
	const actions = normalizeSceneDoActionList(scene.do);
	const conditionRoots = collectSceneConditionRoots(scene);
	const conditionsBroken = conditionRoots.some((root) => check_for_broken_conditions(root));
	return check_for_broken_actions(actions) || conditionsBroken;
};

export const validate_indaily_condition = (indailyint_obj) => {
	let { indailyint } = indailyint_obj;

	if (
		typeof indailyint !== 'object' ||
		!indailyint.dows ||
		indailyint.dows === '' ||
		!indailyint.startat ||
		indailyint.startat === '' ||
		indailyint.dur < 1
	) {
		return false;
	}
	return true;
};
//#region validation of temp elements
export const validate_ui_device_condition_format = (translated: DeviceCondition_UI): boolean => {
	/** Validate keys */
	let required = ['value', 'id', 'cond_type'];
	/**check required*/
	for (let _k of required) {
		if (typeof translated[_k] === 'undefined' || translated[_k] === '') {
			return false;
		}
		if (_k === 'cond_type' && [undefined, ''].includes(translated[_k]?.id?.toString())) {
			return false;
		}
	}

	/**
	 * if found operator, check input value
	 */
	if (typeof translated['operator'] !== 'undefined') {
		//if operator is less than or great than sanitize the input
		if (
			!['', 'equal'].includes(translated['operator']) &&
			typeof translated['input'] === undefined
		) {
			false;
		}
	}
	return true;
};

export const validate_ui_time_based_format = (translated) => {
	if (typeof translated._gui_type === 'undefined' || translated._gui_type === '') {
		return false;
	}

	if (
		!translated.value ||
		(typeof translated.value === 'object' &&
			(translated.value.time === '' || translated.value.time === undefined))
	) {
		return false;
	}
	if (translated._gui_type === 'd_cond' && typeof translated.value === 'object') {
		if (
			typeof translated.value.isin === 'undefined' ||
			translated.value.isin.length === 0 ||
			translated.value.isin.find((e) => isNaN(Number(e)))
		) {
			return false;
		}
	}
	return true;
};

export const validate_ui_device_action_format = (translated) => {
	if (translated.actions === undefined || translated.actions.length === 0) {
		return false;
	}
	return true;
};

export const validate_ui_notify_format = (translated) => {
	if (!translated.msg_type || translated.msg_type === '') {
		return false;
	}
	return true;
};

/** True when `do` is a non-empty array or `{ blk: [...] }` with at least one entry (flow scenes). */
export function hasNonemptySceneDo(doBlock: unknown): boolean {
	if (doBlock == null) return false;
	if (Array.isArray(doBlock)) return doBlock.length > 0;
	if (typeof doBlock === 'object' && Array.isArray((doBlock as any).blk)) {
		return (doBlock as any).blk.length > 0;
	}
	return typeof doBlock === 'object' && Object.keys(doBlock as object).length > 0;
}

export const validate_scene = (scene) => {
	//1. check for conditions
	//2. check for actions
	//3. check meta
	if (!scene.if) {
		//|| !scene.if.and){
		//open modal with alert
		//You don't have set up conditions
		return false;
	}
	//if we have and => search for main OR and check if it has length
	if (scene?.if?.and) {
		let orIndex = _findOrIndex(scene);

		if (orIndex === -1 || scene.if.and[orIndex].or.length === 0) {
			//open modal with alert
			//You don't have set up conditions
			return false;
		}
		/**check the id of OR conditions, because of old UI scene view and creation to avoid wrong scene scripts
		 *and to be sure that there is no any unexpected behaviors -> different scene execution from the old UI */
		if (_findOrIndex(scene) == 0) {
			scene.if.and.reverse();
			// console.log('ON SAVE SCENE reverse indailyint in and');
		}
	} else if (scene?.if?.or?.length === 0) {
		return false;
	}

	if (!hasNonemptySceneDo(scene.do)) {
		return false;
	}

	if (
		!scene._meta ||
		!scene?._meta?.name ||
		scene?._meta?.name === '' ||
		(!scene?._meta?.image && !scene?._meta?.backgroundColor) ||
		(scene?._meta?.image === '' && scene?._meta?.backgroundColor === '')
	)
		return false;

	return true;
};
